<script lang="ts">
import type { Msg, NatsError, Subscription } from "nats.ws";

import { onDestroy, onMount } from "svelte";


    import type {NatsNegConnection, NatsState} from "./natsneg"
    import { bytesToString, stringToBytes } from "./natsnegcommon";
    import type { NatsViewerSession } from "./natsviewersession";
    import SliderControl from "./SliderControl.svelte";
    import type NatsViewer from "./NatsViewer.svelte";

    export let viewerSession: NatsViewerSession | null
    export let neg: NatsNegConnection
    export let peerPub = ""
    export let locationClick: EventListener
    export let setVideoDisabled : (b: boolean) => void

    let cam2label = "Alt"
    let cam1label = "Main"

    let hideControls = true
    let hideAdditionalControls = true
    let gotSettings = true

    let availSettings: {[name: string]: string[]} = {}
    let currentSettings: {[name: string]: string} = {}

    let needRefreshGet = false
    let needAvail = true
    let needGet = true
    let needSet: {[name: string]: string} = {}
    let lastSet: {[name: string]: number} = {}
    let settingHold: {[name: string]: boolean} = {}
    let updatingSettings = false
    let lastRefreshRequest = Date.now() - 12000
    
    let settingsDirty = false
    $: settingsDirty = needAvail || needGet || Object.keys(needSet).length != 0

    let isoLevels: string[] = []
    let exposureNsLevels: string[] = []
    let zoomLevels: string[] = []
    let zoomControl: HTMLInputElement
    let sliderControls: {[name: string]: SliderControl} = {}
    let zoomIndex = 0

    let cameraSwitchInProgress = false

    let sseventSub: Subscription

    let lastError = ""
    let ffcChecked = false
    let audioChecked = false
    let hideVideoChecked = false

    let hadFfc = false

    let latchFfc = (newVal: boolean) => {
        if (newVal) {
            hadFfc = true
        }
        return hadFfc
    }

    let setAvailSettings = (ns: {[name: string]: string[]}) => {
        if (!ns || ns.error) {
            return
        }
        availSettings = ns
        let zoomLevs = ns["zoom"]
        if (zoomLevs != undefined) {
            zoomLevels = zoomLevs
        }
        let isoLevs = ns["iso"]
        if (isoLevs != undefined) {
            isoLevels = isoLevs
        }
        let expLevs = ns["exposure_ns"]
        if (expLevs != undefined) {
            exposureNsLevels = expLevs
        }
        needAvail = false
    }

    let setCurSettings = (ns: {[name: string]: string}) => {
        if (!ns || ns.error) {
            return
        }
        for (let k in ns) {
            if (settingHold[k]) {
                continue
            }
            if (!lastSet[k] || ((lastSet[k] + 500) < lastRefreshRequest)) {
                currentSettings[k] = ns[k]
            }
        }

        let zoom = currentSettings["zoom"]
        if (zoom != undefined) {
            let zoomLevs = availSettings["zoom"]
            if (zoomLevs != undefined) {
                let newIdx = zoomLevs.indexOf(zoom)
                if (newIdx != -1) {
                    zoomIndex = newIdx+1
                }
            }
        }
        if (settingHold["zoom"]) {
            zoomIndex = parseInt(zoomControl.value)
        }
        for (let k in sliderControls) {
            if (settingHold[k]) {
                currentSettings[k] = sliderControls[k].getValue()
            }
        }
        ffcChecked = ns["ffc"] == "on"
        let hrCamName = (s: string, def: string) => {
            let parts = s.split("|")
            if (parts[0] == "hidden") {
                return "Hidden"
            }
            if (parts[0] == "back") {
                return "Back"
            }
            return def
        }
        if (ns["cam1id"]) {
            cam1label = hrCamName(ns["cam1id"], cam1label)
        }
        if (ns["cam2id"]) {
            cam2label = hrCamName(ns["cam2id"], cam2label)
        }
        needGet = false
        needRefreshGet = false
    }

    // DSZ: ipw/settings/mon, WebRTC viewer
    // Controls ipw/settings via NATS
    let settingReq = async (req: any): Promise<any> => {
        let topic = neg.buildMySms("settings", "s", peerPub)
        let error = null
        for (let i = 0; i<3; ++i) {
            try {
                let answer = await neg.nats.request(topic, stringToBytes(JSON.stringify(req)), {timeout: 6000})
                return JSON.parse(bytesToString(answer.data))
            } catch (e) {
                error = e
            }
        }
        lastError = error
        return {'error': error}
    }

    let updateSettings = async (relations?: any) => {
        if (updatingSettings) {
            return
        }
        updatingSettings = true
        try {
            while (true) {
                let didStuff = false
                if (needAvail) {
                    setAvailSettings(await settingReq({action: "avail"}))
                    didStuff = true
                } else if (needGet) {
                    setCurSettings(await settingReq({action: "get"}))
                    didStuff = true
                } else if (Object.keys(needSet).length != 0) {
                    for (let k in needSet) {
                        await settingReq({action: "set", args: {'setting': k, 'value': needSet[k]}})
                        currentSettings[k] = needSet[k]
                        lastSet[k] = Date.now()
                        delete needSet[k]
                        needSet = needSet
                        needRefreshGet = false
                        break
                    }
                    didStuff = true
                } else if (needRefreshGet) {
                    lastRefreshRequest = Date.now()
                    // Same as need get, just different priority
                    setCurSettings(await settingReq({action: "get"}))
                    didStuff = true
                }
                if (!didStuff) {
                    break
                }
            }
        } finally {
            updatingSettings = false
        }
    }
    $: updateSettings([needGet, needSet, needAvail, needRefreshGet])

    interface RateTimer {
        timeoutId: number
        val: string
    }

    let focusHeld = false
    let focusToggle = () => {
        focusHeld = !focusHeld
        ratelimitSet("focus_homing", focusHeld?"on":"off")
    }

    let rateLimitTimers: {[name: string]: RateTimer} = {}
    let ratelimitSet = (setting: string, val: string) => {
        console.log("set", setting, val)
        let oldTimer = rateLimitTimers[setting]
        if (oldTimer != undefined) {
            if (val != oldTimer.val) {
                clearTimeout(oldTimer.timeoutId)
            } else {
                return
            }
        }
        rateLimitTimers[setting] = {
            timeoutId: setTimeout(() => {
                needSet[setting] = val
                needSet = needSet
                delete rateLimitTimers[setting]
            }, 200),
            val: val
        }
    }

    let ratelimitToggle = (setting: string, vals: string[]) => {
        if (currentSettings[setting] == vals[0]) {
            return ratelimitSet(setting, vals[1])
        }
        ratelimitSet(setting, vals[0])
    }

    let setFfc = (checked: boolean) => {
        ratelimitSet("ffc", checked?"on":"off")
        needAvail = true
        needGet = true
        cameraSwitchInProgress = true
    }

    let setAudio = async (checked: boolean) => {
        settingReq({action: "sessctl", args: {sess: viewerSession?.sid, stream_audio: checked}})
    }
    let setVideo = async (checked: boolean) => {
        settingReq({action: "sessctl", args: {sess: viewerSession?.sid, stream_video: checked}})
        setVideoDisabled(!checked)
    }

    function format_ns(tm: number) {
		if (tm > 10000) {
		tm /= 1000.0
		if (tm > 10000) {
			tm /= 1000.0
			if (tm > 10000) {
			tm /= 1000.0
			return Math.round(tm) + "s"
			}
			return Math.round(tm) + "ms"
		}
		return Math.round(tm) + "us"
		}
		return Math.round(tm) + "ns"
	}


    let refreshTimer = setInterval(() => {
        needRefreshGet = true
    }, 6000)
    onMount(() => {
        sseventSub = neg.nats.subscribe(`neg.ssevent.sms.s.${peerPub}.to.v.${neg.pubKey}`)
        sseventSub.callback = (error: NatsError|null, msg: Msg) => {
            let msgData = JSON.parse(bytesToString(msg.data))
            if (!msgData) {
                return
            }
            if (msgData.event == "camera_switched") {
                cameraSwitchInProgress = false
                needAvail = true
                needGet = true
            }
        }
    })
    onDestroy(() => {
        clearInterval(refreshTimer)
        neg?.disconnect()
        if (sseventSub) {
            sseventSub.unsubscribe()
        }
    })

    let holdSetting = (setting: string) => {
        return () => {
            settingHold[setting] = true
        }
    }
    let unholdSetting = (setting: string) => {
        return () => {
            settingHold[setting] = false
        }
    }

</script>

<style>
    .cam-audio-grid-icon *, .pad-2px {
        padding-left: 2px;
        padding-right: 2px;
    }
    .cam-audio-grid {
        display: flex;
        align-items: center;
    }
    .cam-audio-label {
        font-size: 20px;
        margin-bottom: -2px;
        vertical-align: bottom;
    }
    
    .transparent-btn {
        color: white;
        background-color: rgba(0, 0, 0, 0.5);
    }

    .transparent-btn:hover {
        color: #bdc3c7;
    }

    .top-left-abs{
        left: 0; 
        top: 0; 
        position: absolute;
    }

    .hidden {
        display: none;
    }

    .full-width{
        width: 100%;
    }

    .overlay{
        background-color: #0000005c;
        right: 0;
        bottom: 0; 
        position: absolute;
        padding: 10px;
        border-radius: 5px 0px 0px 0px;
        border: solid 1px #00000075;
        text-align: left;
        font-size: 14px;
        z-index: 2;
    }


    .rec-container {
        line-height: 1;
        padding: 10px;
        position: absolute;
        right: 0;
        top: 0;
    }
    .rec-button {
        width: 14px;
        height: 14px;
        font-size: 0;
        background-color: #f00;
        border: 0;
        border-radius: 20px;
        outline: none;
        vertical-align: middle;
        display: inline-block;
    }

    .rec{
        animation: blinker 1s step-end infinite;
    }

    .not-rec{
        background-color: #bdc3c7;
    }

    .rec-button:disabled{
        background-color: #ecf0f1;
    }


    @keyframes blinker {
        50% {
            opacity: 0;
        }
    }

    .button {
        display: inline-block;
        font-weight: 400;
        text-align: center;
        white-space: nowrap;
        vertical-align: middle;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        border: 1px solid transparent;
        padding: 0.3rem 0.5rem;
        border-radius: 0.26rem;
        transition: color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out;
        z-index: 2;
    }

    .btn-danger {
        color: #fff;
        background-color: #c0392b;
        border-color: #c0392b;
    }

    hr{ 
        margin: 4px 0px 4px 0px;
        display: block;
        border: none;
        height: 2px;
    }

    .take-photo:disabled{
        background-color: #27ae60;
        color: #ecf0f1;
    }

    .float-right{
        float: right;
    }

   /*Full width sliders*/
    input[type=range]{
        width: 100%;
    }

    .opt-row {
        padding-top: 4px;
        padding-bottom: 4px;
    }

    .record-grid {
        display: grid;
        grid-template-columns: 1fr auto;
    }

    .record-grid * {
        margin-left: 2px;
        margin-right: 2px;
    }



    .switch-button {
        box-sizing: border-box;
        background: rgba(16, 16, 16, 0.56);
        border-radius: 10px;
        overflow: hidden;
        width: 150px; /* sw-size */
        text-align: center;
        position: relative;
        padding-right: 15px;
        padding-left: 0;
        position: relative;
        height: 20px;
        margin-bottom: -2px;
        /* border: solid 0.5px #333; */
    }
    .switch-button:before {
        content: var(--label);
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        width: 75px; /* sw-size / 2 */
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 3;
        pointer-events: none;
    }
    .switch-button-checkbox {
        cursor: pointer;
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 20px;
        opacity: 0;
        z-index: 2;
    }
    .switch-button-checkbox:checked + .switch-button-label:before {
        transform: translateX(75px) ; /* sw-size / 2 */
        transition: transform 300ms linear;
    }
    .switch-button-checkbox + .switch-button-label {
        position: relative;
        padding: 0;
        display: block;
        user-select: none;
        pointer-events: none;
        width: 75px; /* sw-size / 2 */
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .switch-button-checkbox + .switch-button-label:before {
        content: "";
        background: #0066ffdd;
        border: 1px solid #aaa;
        height: 20px;
        width: 100%;
        position: absolute;
        left: 0;
        top: 0;
        border-radius: 10px;
        transform: translateX(0) ;
        transition: transform 300ms;
    }
    .switch-button-checkbox + .switch-button-label .switch-button-label-span {
        position: relative;
    }

</style>


{#if currentSettings["vrec"] == "on"}
<div class="rec-container">
    <div class="rec-button {(currentSettings["vrec"]=="on"?"rec ":"not-rec")}" style="animation-delay: -{Date.now() % 1000}ms;" disabled={needSet['vrec'] != undefined}></div>
</div>
{/if}

<div class:hidden={!gotSettings} class="button transparent-btn top-left-abs material-icons-outlined" style="line-height: inherit;" on:click={(e) => {hideControls = !hideControls}}>tune</div>



<div class:hidden={hideControls} class="overlay">
    <div class:hidden={hideAdditionalControls}>
    {#if availSettings['iso'] != undefined}
        {#if currentSettings["manual_sensor"] == "on"}
            <SliderControl bind:this={sliderControls["iso"]} title="ISO:" value={isoLevels.indexOf(currentSettings.iso)} id="iso" values={isoLevels}
                display={e => ""}
                on:settled={ev => ratelimitSet("iso", ev.detail.value)}
                on:mousedown={holdSetting("iso")}
                on:mouseup={unholdSetting("iso")}
            />

            <SliderControl bind:this={sliderControls["exposure_ns"]} title="Exposure:" value={exposureNsLevels.indexOf(currentSettings.exposure_ns)} id="exposure_ns" values={exposureNsLevels}
                display={e => ""} 
                on:settled={ev => ratelimitSet("exposure_ns", ev.detail.value)}
                on:mousedown={holdSetting("exposure_ns")}
                on:mouseup={unholdSetting("exposure_ns")}
            />
            <hr>
        {/if}
            
        <div class="opt-row">
            <label for="brightness-btn">Sensor controls:</label>
            <label class="switch float-right">
                <input id="brightness-btn" type="checkbox" checked="{currentSettings["manual_sensor"]=="on"?true:false}" disabled={needSet['manual_sensor'] != undefined} on:click={() => ratelimitToggle("manual_sensor", ["on", "off"])}>
                <span class="slider round"></span> 
            </label>
        </div>
    {/if}
    <div class="opt-row">
        <label for="af-btn">Focus hold:</label>
        <label class="switch float-right">
            <input id="af-btn" type="checkbox" checked={focusHeld} disabled={needSet['focus_homing'] != undefined} on:click={() => focusToggle()}>
            <span class="slider round"></span> 
        </label>
    </div>
    {#if currentSettings["vrec"]}
    <div class="opt-row">
        <label for="rec-btn">Recording:</label>
        <label class="switch float-right">
            <input type="checkbox" id="rec-btn" disabled={needSet['vrec'] != undefined} on:click={() => ratelimitToggle("vrec", ["on", "off"])} checked={currentSettings["vrec"] == "on"}/>
            <span class="slider round custom-rec-slider"></span> 
        </label>
    </div>
    {/if}
    {#if currentSettings["arec"]}
    <div class="opt-row">
        <label for="rec-btn">Audio-only recording:</label>
        <label class="switch float-right">
            <input type="checkbox" id="rec-btn" disabled={needSet['arec'] != undefined} on:click={() => ratelimitToggle("arec", ["on", "off"])} checked={currentSettings["arec"] == "on"}/>
            <span class="slider round custom-rec-slider"></span> 
        </label>
    </div>
    {/if}
    <div class="opt-row">
        <label for="video-btn">Hide video:</label>
        <label class="switch float-right">
            <input id="video-btn" type="checkbox" checked={hideVideoChecked} on:change={(e) => { setVideo(!e.target.checked) }}/>
            <span class="slider round"></span> 
        </label>
    </div>
    <hr>
    </div>
    
    <div class="opt-row record-grid">
        <button 
            class="button {(currentSettings['osd'] == "on") ? "btn-danger" : ""}"
            disabled={needSet['osd'] != undefined} on:click={() => ratelimitSet("osd", "off")}
            style="font-size: smaller;"
        >Hide OSD ({(currentSettings['osd'] == "off") ? "0" : (currentSettings['osd_opacity'] || "100")}%)</button>
        <button class="button transparent-btn material-icons" style="font-size: 14px; line-height: inherit;" on:click={() => {hideAdditionalControls = !hideAdditionalControls}}>{hideAdditionalControls?"expand_less":"expand_more"}</button>
    </div>

    <div class="opt-row">
        <div>Zoom:</div>
        <div>
            {#if zoomLevels.length}
                <input bind:this={zoomControl} disabled={needAvail || cameraSwitchInProgress} type="range" min="1" max={zoomLevels.length} bind:value={zoomIndex} 
                    on:change={(e) => { ratelimitSet("zoom", availSettings["zoom"][e.target.value-1]) }}
                    on:mousedown={holdSetting("zoom")}
                    on:mouseup={unholdSetting("zoom")}
                    />
            {/if}
        </div>
    </div>


    <div class="opt-row cam-audio-grid">
        {#if latchFfc(availSettings["ffc"] && availSettings["ffc"].length > 1)}

        <div style="display: flex;">
            <!-- <label for="cam-btn" class="material-icons cam-audio-label pad-2px">flip_camera_ios</label> -->
            <div class="switch-button pad-2px" style='--label: "{cam2label}";'>
                <input id="cam-btn" class="switch-button-checkbox" type="checkbox" checked={ffcChecked} on:change={(e) => { setFfc(e.target.checked) }}/>
                <label class="switch-button-label" for=""><span class="switch-button-label-span">{cam1label}</span></label>
            </div>
        </div>
        {/if}
        <div class="cam-audio-grid-icon" style="text-align: right;">
            <label for="audio-btn" class="material-icons cam-audio-label">volume_up</label>
            <label class="switch">
                <input id="audio-btn" type="checkbox" checked={audioChecked} on:change={(e) => { setAudio(e.target.checked) }}/>
                <span class="slider round"></span> 
            </label>
        </div>
    </div>
    
    <div class="opt-row">
        <button class="button transparent-btn full-width" on:click={locationClick}>Show on map</button>
    </div>
    <div class="opt-row">
        <button class="button transparent-btn take-photo full-width" disabled={needSet['save_photo'] != undefined} on:click={() => ratelimitSet("save_photo", "on")}>Save photo to Strix</button>
    </div>

    <span>{lastError}</span>
</div>
